import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  DollarSign,
  CheckCircle,
  Phone,
  Mail,
  Calendar,
  Users,
  Building2,
  Zap,
  Shield,
  TrendingUp,
  Target,
  Layers,
  Award,
  MessageSquare,
  Clock,
  BarChart3
} from 'lucide-react';

const Pricing = () => {
  const [practiceSize, setPracticeSize] = useState('small');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const pricingTiers = {
    small: {
      name: "Small Practice / Independent Physicians",
      providers: "1-5 Providers",
      claims: "500-2,000 claims/month",
      baseRate: "4-6%",
      savings: "$30,000 - $70,000",
      features: [
        "Medical billing outsourcing for small practices",
        "Revenue cycle management for independent physicians",
        "Complete charge capture and coding",
        "Insurance eligibility verification",
        "Claim submission and follow-up",
        "Denial management services",
        "Payment posting and reconciliation",
        "Patient billing and collections",
        "Monthly financial reporting",
        "Dedicated account manager"
      ]
    },
    medium: {
      name: "Medium Clinics / Group Practices",
      providers: "6-15 Providers",
      claims: "2,000-5,000 claims/month",
      baseRate: "5-7%",
      savings: "$70,000 - $150,000",
      features: [
        "Denial management services for medical clinics",
        "Multi-provider revenue cycle management",
        "Reduce claim denials in healthcare billing",
        "Specialty-specific coding expertise",
        "Advanced denial prevention strategies",
        "Comprehensive credentialing management",
        "Real-time performance dashboards",
        "Custom reporting and analytics",
        "Quarterly business reviews",
        "Priority support access"
      ]
    },
    large: {
      name: "Multispecialty Practices / Large Clinics",
      providers: "15+ Providers",
      claims: "5,000+ claims/month",
      baseRate: "6-8%",
      savings: "$150,000 - $300,000+",
      features: [
        "Revenue cycle management for multispecialty practices",
        "Benefits of medical billing outsourcing for clinics",
        "Integrated multi-specialty billing platform",
        "Cross-specialty coordination and reporting",
        "Enterprise-level technology access",
        "Dedicated specialty-specific coders",
        "Advanced analytics and benchmarking",
        "Contract negotiation support",
        "Executive-level reporting",
        "White-glove concierge service"
      ]
    }
  };

  const currentTier = pricingTiers[practiceSize];

  return (
    <>
      <Helmet>
        <title>RCM Pricing Plans 2026 | Transparent Billing Costs | MDeRCM</title>
        <meta 
          name="description" 
          content="Compare RCM pricing plans 2026. Transparent billing costs for practices of all sizes. No hidden fees. Free trial available." 
        />
        <meta 
          name="keywords" 
          content="mental health revenue cycle management pricing plans comparison, mental health rcm outsourcing cost, behavioral health rcm outsourcing cost, medical billing outsourcing pricing, revenue cycle management pricing 2026, behavioral health billing cost, best rcm pricing plans, mental health billing services cost, affordable behavioral health rcm, rcm pricing comparison 2026, medical billing costs small practices, how much does medical billing outsourcing cost" 
        />
        <meta property="og:title" content="RCM Pricing Plans 2026 | Transparent Billing Costs | MDeRCM" />
        <meta property="og:description" content="Compare RCM pricing plans 2026. Transparent billing costs for practices of all sizes. No hidden fees." />
        <link rel="canonical" href="https://mdercm.com/pricing" />
      </Helmet>

      <main style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
        
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
          color: '#ffffff',
          padding: isMobile ? '60px 20px' : '80px 40px'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 20px'
          }}>
            <div style={{
              maxWidth: '56rem',
              margin: '0 auto',
              textAlign: 'center'
            }}>
              <h1 style={{
                fontSize: isMobile ? '2rem' : '3rem',
                fontWeight: 'bold',
                marginBottom: '24px',
                fontFamily: "'Poppins', sans-serif"
              }}>
                Transparent, Customized Pricing for Your Practice
              </h1>
              <p style={{
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                marginBottom: '32px',
                opacity: 0.95,
                lineHeight: '1.75'
              }}>
                No One-Size-Fits-All. Every practice is unique, and so is our pricing. Get a customized quote based on your specific needs, volume, and specialty.
              </p>
              
              {/* Key Benefits Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '20px',
                marginTop: '40px'
              }}>
                {[
                  { icon: Target, text: "Customized Solutions" },
                  { icon: TrendingUp, text: "Performance-Based" },
                  { icon: Shield, text: "No Hidden Fees" },
                  { icon: Zap, text: "Flexible Contracts" }
                ].map((item, idx) => (
                  <div key={idx} style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    padding: '16px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <item.icon size={24} />
                    <span style={{ fontSize: '1rem', fontWeight: '500' }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Practice Size Selector */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '40px',
              color: '#111827',
              fontFamily: "'Poppins', sans-serif"
            }}>
              Select Your Practice Size
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '24px',
              marginBottom: '48px'
            }}>
              {['small', 'medium', 'large'].map((size) => {
                const isActive = practiceSize === size;
                const labels = {
                  small: 'Small Practice',
                  medium: 'Medium Clinic',
                  large: 'Large Multispecialty'
                };
                
                return (
                  <div
                    key={size}
                    onClick={() => setPracticeSize(size)}
                    style={{
                      padding: '24px',
                      borderRadius: '12px',
                      border: isActive ? '3px solid #2563eb' : '2px solid #e5e7eb',
                      backgroundColor: isActive ? '#eff6ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: isActive ? '0 10px 25px rgba(37, 99, 235, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      marginBottom: '8px',
                      color: isActive ? '#2563eb' : '#374151'
                    }}>
                      {labels[size]}
                    </h3>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#6b7280'
                    }}>
                      {pricingTiers[size].providers}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Selected Tier Details */}
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '48px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                marginBottom: '32px',
                paddingBottom: '24px',
                borderBottom: '2px solid #e5e7eb'
              }}>
                <div>
                  <h3 style={{
                    fontSize: isMobile ? '1.5rem' : '1.875rem',
                    fontWeight: 'bold',
                    color: '#111827',
                    marginBottom: '8px'
                  }}>
                    {currentTier.name}
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    color: '#6b7280'
                  }}>
                    {currentTier.providers} • {currentTier.claims}
                  </p>
                </div>
                <div style={{
                  textAlign: isMobile ? 'left' : 'right',
                  marginTop: isMobile ? '16px' : '0'
                }}>
                  <div style={{
                    fontSize: isMobile ? '2rem' : '2.5rem',
                    fontWeight: 'bold',
                    color: '#2563eb'
                  }}>
                    {currentTier.baseRate}
                  </div>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#6b7280'
                  }}>
                    of collections per month
                  </p>
                </div>
              </div>

              <div style={{
                backgroundColor: '#f0fdf4',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '32px',
                border: '2px solid #86efac'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '8px'
                }}>
                  <TrendingUp size={24} style={{ color: '#16a34a' }} />
                  <span style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#15803d'
                  }}>
                    Estimated Annual Savings
                  </span>
                </div>
                <p style={{
                  fontSize: isMobile ? '1.5rem' : '2rem',
                  fontWeight: 'bold',
                  color: '#16a34a',
                  marginLeft: '36px'
                }}>
                  {currentTier.savings}
                </p>
              </div>

              <h4 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#111827'
              }}>
                Everything Included:
              </h4>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '12px'
              }}>
                {currentTier.features.map((feature, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <CheckCircle size={20} style={{ color: '#16a34a', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{
                      fontSize: '0.9375rem',
                      color: '#374151',
                      lineHeight: '1.5'
                    }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Customization Section */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '48px'
            }}>
              <h2 style={{
                fontSize: isMobile ? '1.875rem' : '2.25rem',
                fontWeight: 'bold',
                marginBottom: '16px',
                color: '#111827'
              }}>
                100% Customized to Your Practice
              </h2>
              <p style={{
                fontSize: '1.125rem',
                color: '#6b7280',
                maxWidth: '48rem',
                margin: '0 auto'
              }}>
                We analyze your unique situation and build a pricing model that fits your practice perfectly.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '40px'
            }}>
              {/* We Consider */}
              <div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '24px',
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Target size={28} />
                  We Consider:
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {[
                    'Practice size and number of providers',
                    'Specialty and service complexity',
                    'Monthly claim volume',
                    'Current denial rates',
                    'EHR system and integration needs',
                    'Geographic location and payer mix',
                    'Payer contract complexity',
                    'Credentialing requirements',
                    'Existing performance gaps',
                    'Growth and expansion plans'
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px'
                    }}>
                      <CheckCircle size={20} style={{ color: '#2563eb', flexShrink: 0, marginTop: '2px' }} />
                      <span style={{ fontSize: '0.9375rem', color: '#374151' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* You Get */}
              <div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '24px',
                  color: '#16a34a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Award size={28} />
                  You Get:
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {[
                    'Custom pricing based on your needs',
                    'Flexible payment structure',
                    'Volume-based discounts',
                    'Performance incentive alignment',
                    'Transparent cost breakdown',
                    'No surprise fees or charges',
                    'Scalable pricing as you grow',
                    'Month-to-month flexibility option',
                    'Service level customization',
                    'Dedicated implementation support'
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '8px',
                      border: '1px solid #86efac'
                    }}>
                      <CheckCircle size={20} style={{ color: '#16a34a', flexShrink: 0, marginTop: '2px' }} />
                      <span style={{ fontSize: '0.9375rem', color: '#15803d', fontWeight: '500' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Specialized Pricing Cards */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Specialized Pricing Available
            </h2>
            <p style={{
              fontSize: '1.125rem',
              color: '#6b7280',
              textAlign: 'center',
              marginBottom: '40px',
              maxWidth: '48rem',
              margin: '0 auto 40px auto'
            }}>
              We offer customized pricing for specific practice types and needs
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  title: 'Small Practice Billing',
                  link: '/medical-billing-outsourcing-small-practices',
                  icon: Users,
                  rate: '4-6% of collections',
                  highlight: 'Best for 1-5 providers'
                },
                {
                  title: 'Independent Physician RCM',
                  link: '/revenue-cycle-management-independent-physicians',
                  icon: Building2,
                  rate: '5-7% of collections',
                  highlight: 'Solo & small groups'
                },
                {
                  title: 'Multispecialty RCM',
                  link: '/revenue-cycle-management-multispecialty-practices',
                  icon: Layers,
                  rate: '6-8% of collections',
                  highlight: '15+ providers'
                },
                {
                  title: 'Denial Management',
                  link: '/denial-management-services-medical-clinics',
                  icon: Shield,
                  rate: 'Custom pricing',
                  highlight: 'High denial rates'
                },
                {
                  title: 'Reduce Claim Denials',
                  link: '/reduce-claim-denials-healthcare-billing',
                  icon: TrendingUp,
                  rate: 'Performance-based',
                  highlight: '35-45% reduction'
                },
                {
                  title: 'Outsourcing Benefits',
                  link: '/benefits-medical-billing-outsourcing-clinics',
                  icon: Award,
                  rate: '30-50% savings',
                  highlight: 'All practice types'
                }
              ].map((item, idx) => (
                <Link
                  key={idx}
                  to={item.link}
                  style={{
                    textDecoration: 'none',
                    display: 'block'
                  }}
                >
                  <div style={{
                    backgroundColor: '#ffffff',
                    padding: '24px',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    border: '2px solid #e5e7eb',
                    height: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = '#2563eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                  >
                    <item.icon size={32} style={{ color: '#2563eb', marginBottom: '12px' }} />
                    <h3 style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      marginBottom: '8px',
                      color: '#111827'
                    }}>
                      {item.title}
                    </h3>
                    <p style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#2563eb',
                      marginBottom: '8px'
                    }}>
                      {item.rate}
                    </p>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#6b7280'
                    }}>
                      {item.highlight}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included Section */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Everything Included in Every Plan
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '32px'
            }}>
              {[
                {
                  title: 'Core Services',
                  items: [
                    'Complete charge capture',
                    'Medical coding (all specialties)',
                    'Claim submission & follow-up',
                    'Payment posting',
                    'Patient billing',
                    'AR management'
                  ]
                },
                {
                  title: 'Technology & Tools',
                  items: [
                    'Advanced billing software',
                    'EHR integration',
                    'Real-time dashboards',
                    'Custom reporting',
                    'Analytics & insights',
                    'Secure data exchange'
                  ]
                },
                {
                  title: 'Support & Expertise',
                  items: [
                    'Dedicated account manager',
                    'Certified coding team',
                    'Denial management experts',
                    'Credentialing support',
                    'Regular business reviews',
                    'Training & consultation'
                  ]
                }
              ].map((section, idx) => (
                <div key={idx}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    marginBottom: '20px',
                    color: '#2563eb'
                  }}>
                    {section.title}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {section.items.map((item, itemIdx) => (
                      <div key={itemIdx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}>
                        <CheckCircle size={20} style={{ color: '#16a34a', flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ fontSize: '0.9375rem', color: '#374151' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ROI Calculator Teaser */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#15803d'
            }}>
              Calculate Your Potential ROI
            </h2>
            <p style={{
              fontSize: '1.125rem',
              color: '#166534',
              marginBottom: '32px',
              maxWidth: '48rem',
              margin: '0 auto 32px auto'
            }}>
              Most practices see significant returns within the first 90 days
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '24px',
              marginBottom: '32px'
            }}>
              {[
                { label: 'Cost Savings', value: '$40K - $150K', sublabel: 'Annually' },
                { label: 'Additional Revenue', value: '$150K - $250K', sublabel: 'From improved collections' },
                { label: 'Average ROI', value: '200-300%', sublabel: 'Within first year' }
              ].map((stat, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#16a34a',
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    {stat.label}
                  </div>
                  <div style={{
                    fontSize: isMobile ? '1.75rem' : '2.25rem',
                    fontWeight: 'bold',
                    color: '#15803d',
                    marginBottom: '4px'
                  }}>
                    {stat.value}
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#166534'
                  }}>
                    {stat.sublabel}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact/CTA Section */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          background: 'linear-gradient(135deg, #1e40af 0%, #3730a3 100%)',
          color: '#ffffff'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              Ready to Get Your Custom Quote?
            </h2>
            <p style={{
              fontSize: '1.125rem',
              marginBottom: '40px',
              opacity: 0.95,
              maxWidth: '48rem',
              margin: '0 auto 40px auto'
            }}>
              Contact us today for a personalized pricing proposal based on your practice's unique needs
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '24px',
              marginBottom: '32px'
            }}>
              <a
                href="tel:+15103566069"
                style={{
                  textDecoration: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '24px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                }}
              >
                <Phone size={32} style={{ color: '#ffffff' }} />
                <div style={{ color: '#ffffff' }}>
                  <div style={{ fontSize: '0.875rem', marginBottom: '4px', opacity: 0.9 }}>Call Us</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>+1 (510) 356-6069</div>
                </div>
              </a>

              <a
                href="mailto:sales@mdercm.com"
                style={{
                  textDecoration: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '24px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                }}
              >
                <Mail size={32} style={{ color: '#ffffff' }} />
                <div style={{ color: '#ffffff' }}>
                  <div style={{ fontSize: '0.875rem', marginBottom: '4px', opacity: 0.9 }}>Email Us</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>sales@mdercm.com</div>
                </div>
              </a>

              <Link
                to="/free-trial"
                style={{
                  textDecoration: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '24px',
                  backgroundColor: '#F26522',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#d3541c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#F26522';
                }}
              >
                <Calendar size={32} style={{ color: '#ffffff' }} />
                <div style={{ color: '#ffffff' }}>
                  <div style={{ fontSize: '0.875rem', marginBottom: '4px' }}>Schedule a Call</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>Book Free Consultation</div>
                </div>
              </Link>
            </div>

            <div style={{
              padding: '24px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '12px',
              marginTop: '24px'
            }}>
              <p style={{
                fontSize: '1.125rem',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                🎁 Special Offer
              </p>
              <p style={{
                fontSize: '1rem',
                opacity: 0.95
              }}>
                First 30 days FREE for new clients. No setup fees. No long-term contracts required.
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '40px',
              color: '#111827'
            }}>
              Pricing FAQs
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {[
                {
                  q: 'How is pricing determined?',
                  a: 'Pricing is customized based on your practice size, specialty, claim volume, current performance metrics, and specific service needs. We analyze your unique situation to create a fair, transparent pricing model.'
                },
                {
                  q: 'Are there any hidden fees?',
                  a: 'No. Our pricing is completely transparent. The percentage rate covers all standard services including coding, claim submission, AR management, denial management, reporting, and support. Any additional services are clearly outlined upfront.'
                },
                {
                  q: 'What if my volume changes?',
                  a: 'Our percentage-based model automatically scales with your volume. If collections increase, you pay more, but you\'re also earning more. If collections decrease, your costs decrease proportionally. It\'s completely fair and flexible.'
                },
                {
                  q: 'Can I customize which services I need?',
                  a: 'Absolutely! While we offer comprehensive RCM solutions, you can choose specific services based on your needs. We\'ll adjust pricing accordingly to ensure you only pay for what you need.'
                },
                {
                  q: 'Do you require long-term contracts?',
                  a: 'We offer both month-to-month and annual contract options. Many practices prefer annual contracts for better rates, but we understand flexibility is important. We\'ll work with what makes sense for your practice.'
                },
                {
                  q: 'When will I see ROI?',
                  a: 'Most practices see measurable improvements within 60-90 days. Cost savings are typically immediate (staff, software, overhead), while revenue improvements from better collections and reduced denials build over the first few months. Full ROI is usually achieved within 6-12 months.'
                }
              ].map((faq, idx) => (
                <div key={idx} style={{
                  padding: '24px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <MessageSquare size={20} style={{ color: '#2563eb' }} />
                    {faq.q}
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    color: '#4b5563',
                    lineHeight: '1.75',
                    marginLeft: '28px'
                  }}>
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Pages */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.5rem' : '1.875rem',
              fontWeight: '600',
              textAlign: 'center',
              marginBottom: '32px',
              color: '#111827'
            }}>
              Learn More About Our Solutions
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px'
            }}>
              {[
                { title: 'Small Practice Billing', link: '/medical-billing-outsourcing-small-practices' },
                { title: 'Independent Physician RCM', link: '/revenue-cycle-management-independent-physicians' },
                { title: 'Multispecialty RCM', link: '/revenue-cycle-management-multispecialty-practices' },
                { title: 'Denial Management', link: '/denial-management-services-medical-clinics' },
                { title: 'Reduce Claim Denials', link: '/reduce-claim-denials-healthcare-billing' },
                { title: 'Outsourcing Benefits', link: '/benefits-medical-billing-outsourcing-clinics' }
              ].map((page, idx) => (
                <Link
                  key={idx}
                  to={page.link}
                  style={{
                    padding: '20px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#2563eb';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: '#2563eb'
                  }}>
                    {page.title} →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default Pricing;